import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  Bandeja, ResumenSolicitud, SolicitudesAltaService, diasDesde,
} from 'src/app/services/workflows/solicitudes-alta.service';
import { mensajeDeError } from 'src/app/services/workflows/workflows.service';

export type CorteBandeja = 'me_tocan' | 'por_tomar' | 'de_otros' | 'todas';
const CORTES_VALIDOS: CorteBandeja[] = ['me_tocan', 'por_tomar', 'de_otros', 'todas'];

/**
 * «Altas de comercios» — la bandeja. Cuatro cortes de la misma cola: lo mío, lo que espera a mi
 * perfil, lo que otro de mi perfil ya tomó, y el histórico completo. Maqueta aprobada por Juan el
 * 2026-09-28 (vista 4, CONTRATO-ALTAS.md).
 */
@Component({
  selector: 'app-altas-bandeja',
  templateUrl: './altas-bandeja.component.html',
  styleUrls: ['./altas-bandeja.component.css'],
})
export class AltasBandejaComponent implements OnInit {
  cargando = true;
  error = '';
  corte: CorteBandeja = 'me_tocan';
  bandeja: Bandeja | null = null;

  // «Todas» se pide aparte, solo cuando se elige ese corte (es la única lista que puede ser larga).
  listaTodas: ResumenSolicitud[] | null = null;
  cargandoTodas = false;
  errorTodas = '';

  // El buscador (contrato D): filtra en la página la lista ya cargada del corte activo, por
  // comercio o por RIF. Se limpia solo al cambiar de corte, para no dejar un filtro viejo puesto
  // sobre una lista distinta.
  busqueda = '';

  constructor(
    private _alta: SolicitudesAltaService,
    private route: ActivatedRoute,
    private router: Router,
  ) { }

  ngOnInit(): void {
    // El corte vive en el query param: así una pestaña se comparte y el back del navegador funciona.
    this.route.queryParamMap.subscribe((pm) => this.alCambiarCorte(pm.get('corte') as CorteBandeja | null));
  }

  private huboPrimeraCarga = false;
  private alCambiarCorte = async (parametro: CorteBandeja | null): Promise<void> => {
    this.busqueda = '';
    if (!this.huboPrimeraCarga) {
      this.huboPrimeraCarga = true;
      await this.cargarBandeja(parametro);
      return;
    }
    // La bandeja ya está: solo cambia el corte visible (y trae «todas» si hace falta).
    this.corte = this.esCorteValido(parametro) ? parametro : 'me_tocan';
    if (this.corte === 'todas' && this.listaTodas === null) await this.cargarTodas();
  };

  private esCorteValido = (c: string | null): c is CorteBandeja => CORTES_VALIDOS.includes(c as CorteBandeja);

  private cargarBandeja = async (parametroUrl: CorteBandeja | null): Promise<void> => {
    this.cargando = true;
    this.error = '';
    try {
      this.bandeja = await this._alta.bandeja();
      if (this.esCorteValido(parametroUrl)) {
        this.corte = parametroUrl;
      } else {
        // Por omisión «Asignadas a mí»; si viene vacía y «Sin asignar» tiene algo, ahí se abre.
        this.corte = (!this.bandeja.me_tocan.length && this.bandeja.por_tomar.length) ? 'por_tomar' : 'me_tocan';
      }
      if (this.corte === 'todas') await this.cargarTodas();
    } catch (e: any) {
      this.bandeja = null;
      this.error = mensajeDeError(e, 'No se pudieron traer las solicitudes de alta.');
    } finally {
      this.cargando = false;
    }
  };

  private cargarTodas = async (): Promise<void> => {
    this.cargandoTodas = true;
    this.errorTodas = '';
    try {
      this.listaTodas = await this._alta.todas();
    } catch (e: any) {
      this.listaTodas = null;
      this.errorTodas = mensajeDeError(e, 'No se pudieron traer todas las solicitudes.');
    } finally {
      this.cargandoTodas = false;
    }
  };

  /** Reintentar tras un error: la bandeja entera, o solo «todas» si fue esa la que falló. */
  reintentar = (): void => {
    if (this.error) { this.huboPrimeraCarga = false; this.cargarBandeja(this.corte); return; }
    if (this.errorTodas) this.cargarTodas();
  };

  /** La lista que corresponde al corte activo. */
  get filas(): ResumenSolicitud[] {
    if (!this.bandeja) return [];
    switch (this.corte) {
      case 'me_tocan': return this.bandeja.me_tocan;
      case 'por_tomar': return this.bandeja.por_tomar;
      case 'de_otros': return this.bandeja.de_otros;
      case 'todas': return this.listaTodas || [];
    }
  }

  /** Lo que se ve en la tabla: la lista del corte activo, filtrada por comercio o RIF. Comercio se
   *  compara como texto llano; el RIF, sin letra de mayúscula ni guiones ni puntos, para que
   *  «40123456» o «j40123456» encuentren igual a «J-40.123.456-7». */
  get filasFiltradas(): ResumenSolicitud[] {
    const q = this.busqueda.trim();
    if (!q) return this.filas;
    const qTexto = q.toLowerCase();
    const qRif = q.toUpperCase().replace(/[^A-Z0-9]/g, '');
    return this.filas.filter((f) => {
      const comercio = (f.comercio || '').toLowerCase().includes(qTexto);
      const rif = !!qRif && (f.rif || '').toUpperCase().replace(/[^A-Z0-9]/g, '').includes(qRif);
      return comercio || rif;
    });
  }

  get mensajeSinResultados(): string {
    return `No hay ninguna solicitud que coincida con «${this.busqueda.trim()}».`;
  }

  /** El núcleo lo dice directo: nadie tiene ninguno de los perfiles del workflow del alta.
   * (Antes se adivinaba con los tres cortes vacíos, y eso también daba vacío sin solicitudes pendientes.) */
  get sinPerfilDeAlta(): boolean {
    return !!this.bandeja && this.bandeja.tengo_perfil === false;
  }

  get mensajeVacio(): string {
    switch (this.corte) {
      case 'me_tocan': return 'No tienes ninguna solicitud asignada. Las de tus perfiles que nadie tomó están en «Sin asignar».';
      case 'por_tomar': return 'No hay solicitudes esperando a tu perfil.';
      case 'de_otros': return 'Ninguna solicitud de tus perfiles está asignada a otra persona.';
      case 'todas': return 'Todavía no hay solicitudes de alta.';
    }
  }

  /** Días en la etapa actual; null si la solicitud no tiene etapa (terminada). */
  diasEnEtapa(f: ResumenSolicitud): number | null {
    return f.etapa ? diasDesde(f.etapa.desde) : null;
  }

  /** La insignia de una fila APROBADA (CONTRATO-PUESTA-EN-MARCHA.md): mientras exista
   *  `puesta_estado`, manda ella, no el «alta» viejo — igual criterio que en la solicitud. */
  insigniaAprobada(f: ResumenSolicitud): { texto: string; clase: string } {
    if (f.puesta_estado === 'OPERATIVA') return { texto: 'Operativa', clase: 'kb-badge-exito' };
    if (f.puesta_estado === 'EN_CURSO') return { texto: 'Aprobada · poniéndose en marcha', clase: 'kb-badge-neutro' };
    if (f.puesta_estado === 'FALLO') return { texto: 'Aprobada · con pendientes', clase: 'kb-badge-alerta' };
    // Solicitudes de antes de este contrato, sin `puesta_estado`.
    return { texto: f.efecto_estado === 'PENDIENTE' ? 'Aprobada · alta pendiente' : 'Aprobada', clase: 'kb-badge-exito' };
  }

  /** Quién la tiene. El núcleo ya dice `es_mia` directo (antes se adivinaba comparando el
   * responsable con el usuario de la sesión). Si ya terminó (aprobada o rechazada), no está en
   * ninguna bandeja: no hay responsable que mostrar. */
  laTiene(f: ResumenSolicitud): string {
    if (f.es_mia) return 'Tú';
    if (f.estado !== 'EN_CURSO') return '—';
    return f.responsable?.username || 'Nadie (en la bandeja)';
  }

  porId = (_i: number, f: ResumenSolicitud): string => f._id;

  /** Enter abre la solicitud igual que el clic (la fila hermana de Workflows hace lo mismo). */
  irA(f: ResumenSolicitud): void {
    if (f.enlace) { this.router.navigateByUrl(f.enlace); return; }   // la firma de tanda abre su ficha de liquidación
    this.router.navigate(['/console/altas', f._id]);
  }
}
